import axios from "axios";
import { produce } from "immer";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import { updateStageAttribute } from "./helper";
import namespaceAction from "../../reducers/helpers";
import { createAgreements, createAddendums, formattedStages } from "./apiAdapters";

const REDUCER_NAME = "AgreementReducer";
const wrap = namespaceAction(REDUCER_NAME);

export const agreementUploaded = (data) => wrap({ type: "AGREEMENT_UPLOADED", data });

export const agreementUpdated = (data, id) => wrap({ type: "AGREEMENT_UPDATED", data, id });

export const loanUpdated = (data, id) => wrap({ type: "LOAN_UPDATED", data, id });

export const agreementReplaced = (data, agreementId) =>
  wrap({ type: "AGREEMENT_REPLACED", data, agreementId });

export const removeDeletedAgreement = (id) => wrap({ type: "DELETE_AGREEMENT", id });

export const toggleSuccessToastMsg = (data) => wrap({ type: "TOGGLE_SUCCESS_TOAST_MSG", data });

export const storeFetchedAgreements = (data) => wrap({ type: "FETCH_AGREEMENTS", data });

export const storeFetchedLoans = (data) => wrap({ type: "FETCH_LOANS", data });

export const storeOnboardingStatus = (data) => wrap({ type: "FETCH_ONBOARDING_STATUS", data });

export const storeFetchedLoansFinancingStages = (data) =>
  wrap({ type: "FETCH_LOANS_FINANCING_STAGES", data });

export const storeFetchedAvailableTransactions = (data) =>
  wrap({ type: "FETCH_AVAILABLE_TRANSACTIONS", data });

export const storeFetchedAvailableTransactionsForLoan = (data) =>
  wrap({ type: "FETCH_AVAILABLE_TRANSACTIONS_LOAN", data });

export const storeFetchedAddendums = (data) => wrap({ type: "FETCH_ADDENDUMS", data });

export const storeSellScores = (sellScores) => ({
  type: "STORE_SELL_SCORES",
  sellScores,
});

const updateTagsSuccess = (data) => {
  return {
    type: "UPDATE_TAGS_SUCCESS",
    tags: data,
  };
};

const toggleTagsError = (value) => {
  return {
    type: "TOGGLE_TAGS_ERROR",
    value,
  };
};

const updatePersonSuccess = (data) => {
  return {
    type: "UPDATE_PERSON_SUCCESS",
    person: data,
  };
};

export const updatePrimaryAgent = (primaryAgentUuid) => {
  return {
    type: "UPDATE_PRIMARY_AGENT",
    primaryAgentUuid,
  };
};

const togglePersonError = (value) => {
  return {
    type: "TOGGLE_PERSON_ERROR",
    value,
  };
};

export const updatePersonValues = (name, value) => {
  return {
    type: "UPDATE_PERSON_VALUES",
    name,
    value,
  };
};

export const openEditPersonModal = () => {
  return {
    type: "OPEN_EDIT_PERSON_MODAL",
  };
};

export const closeEditPersonModal = () => {
  return {
    type: "CLOSE_EDIT_PERSON_MODAL",
  };
};

const fetchInitialTimeline = (data) => {
  return {
    type: "FETCH_INITIAL_TIMELINE",
    data: data,
  };
};

const fetchTimelineError = (data) => {
  return {
    type: "FETCH_TIMELINE_ERROR",
    data: data,
  };
};

const fetchingTimeline = () => {
  return {
    type: "FETCHING_TIMELINE",
  };
};

export const updateTimeline = (data, type) => {
  return {
    type: type,
    data: data,
  };
};

const updateTimelineEvent = (data) => {
  return {
    type: "SUBMIT_UPDATE_TIMELINE_EVENT",
    data: data,
  };
};

const deleteTimelineNote = (id) => {
  return {
    type: "DELETE_TIMELINE_NOTE",
    id,
  };
};

const submitSendEmail = (data) => {
  return {
    type: "SUBMIT_SEND_EMAIL",
    data: data,
  };
};

const clearEmailState = (bool) => {
  return {
    type: "CLEAR_EMAIL_STATE",
    bool: bool,
  };
};

const submitEmailError = (data) => {
  return {
    type: "SUBMIT_ERROR_EMAIL",
    data: data,
  };
};

const submitAddAppointment = (data) => {
  return {
    type: "SUBMIT_ADD_APPOINTMENT",
    data: data,
  };
};

export const submitNoteSuccess = (note) => ({
  type: "SUBMIT_NOTE_SUCCESS",
  note,
});

export const updateNoteSuccess = (note) => ({
  type: "UPDATE_NOTE_SUCCESS",
  note,
});

export const deleteNoteSuccess = (note) => ({
  type: "DELETE_NOTE_SUCCESS",
  note,
});

// use to reload person Detail after Edit Contact modal close
export const reloadPerson = (personId) => {
  return (dispatch) => {
    axios
      .get(`/api/v4/person_detail/show/${personId}`, {
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        dispatch(updatePersonSuccess(res.data));
      });
  };
};

export const simpleUpdatePerson = (person) => (dispatch) =>
  axios
    .patch(
      `/api/v4/contact_people/${person.id}`,
      { person },
      { headers: { "x-csrf-token": ReactOnRails.authenticityToken() } },
    )
    .then((res) => {
      dispatch(
        updatePersonSuccess({
          data: {
            attributes: Object.keys(person).reduce((acc, key) => ({ ...acc, [key]: res.data[key] }), {}),
          },
        }),
      );
    });

export const updatePerson = (editablePerson) => {
  const updatedPerson = produce(editablePerson, (draft) => {
    updateStageAttribute(draft);
  });

  return (dispatch) =>
    axios
      .patch(`/api/v4/person_detail/${updatedPerson.data.id}`, {
        person: updatedPerson,
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        dispatch(updatePersonSuccess(res.data));
      });
};

export const updatePersonData = (updatedPersonData) => {
  return (dispatch) => {
    dispatch(updatePersonSuccess(updatedPersonData));
  };
};

export const uploadPersonAvatar = (event, person) => {
  return (dispatch) => {
    const fileUploaded = event.target.files[0];
    const url = "/api/v4/person_detail/upload_avatar/" + person.data.attributes.slug;
    let form_data = new FormData();
    form_data.append("authenticity_token", ReactOnRails.authenticityToken());
    form_data.append("person[avatar]", fileUploaded);
    axios
      .post(url, form_data)
      .then((res) => {
        dispatch(updatePersonSuccess(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const addTagToPerson = (tag, person) => {
  return (dispatch) => {
    const url = "/api/v4/person_detail/create_tag/" + person.data.attributes.id;
    axios
      .post(url, { tag_name: tag, authenticity_token: ReactOnRails.authenticityToken() })
      .then((res) => {
        dispatch(updatePersonSuccess(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const deleteTagFromPerson = (tag_id, person) => {
  return (dispatch) => {
    const url = "/api/v4/person_detail/destroy_tag/" + person.data.attributes.id;
    axios
      .delete(url, { data: { tag_id: tag_id, authenticity_token: ReactOnRails.authenticityToken() } })
      .then((res) => {
        dispatch(updatePersonSuccess(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const submitNote = (note) => {
  return (dispatch) => {
    axios
      .post(`/people/${note.notable_id}/notes`, {
        note: { ...note },
        person_details_page: "true",
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        dispatch(submitNoteSuccess(res.data));
        dispatch(closeAddNoteModal());
        dispatch(updateTimeline(res.data.timeline, "UPDATE_PUBLIC_ACTIVITY_EVENTS"));
      })
      .catch((err) => {
        console.log(err);
      });
  };
};

export const updateNote = (note) => {
  return (dispatch) => {
    axios
      .patch(`/people/${note.notable_id}/notes/${note.id}`, {
        note,
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        dispatch(updateNoteSuccess(res.data));
        dispatch(updateTimelineEvent(res.data.timeline, "UPDATE_PUBLIC_ACTIVITY_EVENTS"));
      })
      .catch((err) => {
        console.log(err);
      });
  };
};

export const getNotes = (person) => {
  return (dispatch) => {
    axios
      .get(`/people/${person.data.id}/notes.json`)
      .then((res) => {
        dispatch(getNotesSuccess(res.data));
      })
      .catch((err) => {
        console.log(err);
      });
  };
};

export const deleteNote = (note) => {
  return (dispatch) => {
    axios
      .delete(`/people/${note.notable_id}/notes/${note.id}`, {
        data: {
          authenticity_token: ReactOnRails.authenticityToken(),
        },
      })
      .then((res) => {
        dispatch(deleteNoteSuccess(note));
        dispatch(deleteTimelineNote(note.id));
      })
      .catch((err) => {
        console.log(err);
      });
  };
};

export const submitUpdateNote = (value, event) => {
  return (dispatch) => {
    axios
      .patch(`/people/${event.object_attributes.notable_id}/notes/${event.object_attributes.id}`, {
        note: { content: value },
        person_details_page: "true",
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        dispatch(updateTimelineEvent(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const submitUpdateInteraction = (interaction) => {
  return (dispatch) => {
    dispatch(updateTimelineEvent(interaction));
  };
};

export const submitEmail = (sentEmailData, appInviteNeedsUpdating = null, homeAppRecipientId = null) => {
  return (dispatch) => {
    dispatch(submitSendEmail(sentEmailData));

    if (appInviteNeedsUpdating) {
      dispatch(touchHomeAppInviteSent(homeAppRecipientId));
    }

    setTimeout(function () {
      dispatch(clearEmailState(false));
    }, 5000);
  };
};

export const handleSubmitEmailError = (err) => {
  return (dispatch) => {
    const data = err?.response?.data;
    let errors = data?.content || data?.error || "Whoops something went wrong! Try again later.";
    if (typeof data?.errors === "object") {
      const firstKey = Object.keys(data.errors)[0];
      errors = `${firstKey} ${data.errors[firstKey].join(", ")}`;
    }
    dispatch(submitEmailError({ errors, isSupportError: data?.content?.startsWith("We apologize") }));
  };
};

export const submitAppointment = (data) => {
  return (dispatch) => {
    dispatch(submitAddAppointment(data));
  };
};

export const openAddAppointmentModal = () => {
  return {
    type: "OPEN_ADD_APPOINTMENT_MODAL",
  };
};

export const closeAddAppointmentModal = () => {
  return {
    type: "CLOSE_ADD_APPOINTMENT_MODAL",
  };
};

export const addSmsToTimeline = (smsEvent) => {
  return (dispatch) => {
    dispatch(updateTimeline(smsEvent, "UPDATE_PUBLIC_ACTIVITY_EVENTS"));
  };
};

export const submitOtherInteraction = (url, brivityOtherInteractionWrapper) => {
  return (dispatch) => {
    axios
      .post(url, brivityOtherInteractionWrapper)
      .then((res) => {
        dispatch(updateTimeline(res.data, "UPDATE_PUBLIC_ACTIVITY_EVENTS"));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const openManageAccessModal = () => ({ type: "OPEN_MANAGE_ACCESS_MODAL" });

export const closeManageAccessModal = () => ({ type: "CLOSE_MANAGE_ACCESS_MODAL" });

export const fetchTimeline = (slug) => {
  return (dispatch) => {
    dispatch(fetchingTimeline());
    axios
      .get(`/people/${slug}/timeline?remote=true&person_details_page=true`, {
        validateStatus: function (status) {
          return status < 400; // Resolve only if the status code is less than 400
        },
      })
      .then((res) => {
        dispatch(fetchInitialTimeline(res.data));
      })
      .catch((err) => {
        dispatch(fetchTimelineError(err));
      });
  };
};

export const openAddRelationShipModal = () => ({ type: "OPEN_ADD_RELATIONSHIP_MODAL" });

export const closeAddRelationShipModal = () => ({ type: "CLOSE_ADD_RELATIONSHIP_MODAL" });

export const openAddNoteModal = () => ({ type: "OPEN_ADD_NOTE_MODAL" });

export const closeAddNoteModal = () => ({ type: "CLOSE_ADD_NOTE_MODAL" });

const uploadingDocument = (bool) => {
  return {
    type: "UPLOADING_DOCUMENT",
    bool,
  };
};

const documentUploaded = (data) => {
  return {
    type: "DOCUMENT_UPLOADED",
    data: data,
  };
};

const storeFetchedDocments = (data) => {
  return {
    type: "FETCH_DOCUMENTS",
    data: data,
  };
};

const removeDeletedDocument = (id) => {
  return {
    type: "DELETE_DOCUMENT",
    id,
  };
};

export const removeDocAgreement = (id) => {
  return {
    type: "DELETE_DOCUMENT_AGREEMENT",
    id,
  };
};

export const renameDocument = (id, newName) => {
  return {
    type: "RENAME_DOCUMENT",
    id,
    newName,
  };
};

export const createTransactions = (transactionData) => {
  return async (dispatch) => {
    try {
      const {
        selectedAgreementName,
        propertyType,
        closePrice,
        transactionTitle,
        source,
        client_id,
        clientIntent,
        referringAgent,
        referralFee,
        referral_partner_id,
        commission,
        commission_percentage,
        referralFeeIsPercentage,
        personId,
        personRoleId,
        agents,
      } = transactionData;

      const AGREEMENT_NAME_HASH = {
        buyer: "BuyerListing",
        seller: "SellerListing",
        referral: "ReferralListing",
      };

      const formData = new FormData();

      formData.append("utf8", "✓");
      formData.append("authenticity_token", ReactOnRails.authenticityToken());
      formData.append("listing[type]", AGREEMENT_NAME_HASH[selectedAgreementName]);

      formData.append(
        "listing[status]",
        selectedAgreementName === "buyer" || selectedAgreementName === "seller" ? "pipeline" : "active",
      );
      formData.append("listing[title]", transactionTitle);
      if (selectedAgreementName !== "referral") {
        formData.append(`listing[viewers_attributes][${personId}][person_id]`, personId);
        formData.append(`listing[viewers_attributes][${personId}][role_id]`, personRoleId);
        formData.append(`listing[viewers_attributes][${personId}][visible_to_public]`, true);
        formData.append(`listing[viewers_attributes][${personId}][collaborator]`, false);
        formData.append(`listing[viewers_attributes][${personId}][gets_updates]`, false);
      }
      if (selectedAgreementName === "buyer" || selectedAgreementName === "seller") {
        formData.append("listing[est_close_price]", closePrice);
        formData.append("listing[commission]", commission);
        formData.append("listing[commission_percentage]", commission_percentage);
        formData.append("listing[source_id]", source?.value);

        for (let i = 0; i < agents.length; i += 1) {
          const agent = agents[i];

          formData.append(`listing[team_members_attributes][${agent.value}${i}][person_id]`, agent.value);
          formData.append(`listing[team_members_attributes][${agent.value}${i}][role_id]`, agent.roleId);
          formData.append(
            `listing[team_members_attributes][${agent.value}${i}][visible_to_public]`,
            agent.visible,
          );
          formData.append(`listing[team_members_attributes][${agent.value}${i}][primary]`, agent.primary);
        }
        formData.append("listing[address_attributes][address_type]", "listing");
        formData.append("property_type", propertyType.value);
      } else {
        formData.append("listing[referral_attributes][client_id]", client_id);
        formData.append("listing[referral_attributes][client_intent]", clientIntent);
        formData.append("listing[referral_attributes][referring_agent_id]", referringAgent);

        if (referralFee.length) {
          formData.append("listing[referral_attributes][referral_fee]", referralFee);
        }
        formData.append("listing[referral_attributes][is_fee_percentage]", referralFeeIsPercentage);
        if (referral_partner_id) {
          formData.append("listing[referral_attributes][referral_partner_id]", referral_partner_id);
        }
        // seller
        if (clientIntent === "seller") {
          formData.append("property_type", propertyType.value);
          formData.append("listing[address_attributes][address_type]", "listing");
        }
      }

      if (selectedAgreementName === "buyer") {
        formData.append("listing[only_pending_info]", "true");
      }
      formData.append("return_listing_details", "true");
      const res = await axios.post("/listings", formData);

      return res.data?.header?.id;
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      return null;
    }
  };
};

export const loadRoles = async (selectedAgreementName, currentUserID) => {
  const path =
    selectedAgreementName === "buyer"
      ? "/roles/pending_transaction_roles"
      : "/roles/listing_transaction_roles";

  const response = await axios.get(path, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  let defaultRoles = response.data.default_roles;
  const { roles } = response.data;

  // Reorder listing or buyer's agent role to be first
  if (selectedAgreementName === "buyer") {
    const buyersAgentRole = roles.find((role) => role.label === "Buyer's Agent");

    defaultRoles = [
      buyersAgentRole,
      ...defaultRoles.filter((defaultRole) => defaultRole.label !== "Buyer's Agent"),
    ];
  } else {
    const listingAgentRole = roles.find((role) => role.label === "Listing Agent");
    defaultRoles = [
      listingAgentRole,
      ...defaultRoles.filter((defaultRole) => defaultRole.label !== "Listing Agent"),
    ];
  }

  // Undefined shows up if the account doesn't have "Buyer's Agent" or "Listing Agent"

  defaultRoles = defaultRoles.filter((defaultRole) => defaultRole !== undefined);

  return {
    primaryAgentsOnTransaction: defaultRoles.map((role, index) => {
      const matchedAgent = role.agents.find((agent) => String(agent.value) === String(currentUserID));
      return {
        ...(matchedAgent ? { label: matchedAgent.label, value: matchedAgent.value } : {}),
        roleId: role.value,
        visible: "false",
        primary: index === 0 ? "true" : "false",
      };
    }),
    agentsOnTransaction: defaultRoles.map((role) => ({
      ...(role.agents[0].label.includes("Role Default") ? role.agents[0] : null),
      roleId: role.value,
      visible: "false",
      primary: "false",
    })),
    roles,
  };
};
export const loadAllAvailableRoles = async () => {
  const response = await axios.get("/api/v4/transactions/allAvailableRoles/people/roles");
  return response.data?.client || [];
};

export const loadSources = async () => {
  const response = await axios.get("/sources");
  return response.data.sources.map((source) => ({ label: source.text, value: source.id }));
};

export const loadPropertyTypes = async () => {
  const response = await axios.get("/transaction_property_types");
  return response.data.property_types.map((propertyType) => ({
    label: propertyType.name,
    value: propertyType.id,
  }));
};

export const fetchDocuments = (person) => {
  return (dispatch) => {
    axios
      .get(`/people/${person.id}/documents`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeFetchedDocments(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const fetchAddendums = (uuid, agreementId) => {
  return (dispatch) => {
    axios
      .get(`/api/v4/people/${uuid}/agreements/${agreementId}/documents`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeFetchedAddendums(createAddendums(res.data)));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const fetchAgreements = (person) => {
  return (dispatch) => {
    axios
      .get(`/api/v4/people/${person.attributes.uuid}/agreements`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeFetchedAgreements(createAgreements(res.data)));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const fetchLoans = (personId) => {
  return (dispatch) => {
    axios
      .get(`/api/v4/contact_people/${personId}/loans`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeFetchedLoans(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const getMarketerOnboardingStatus = () => {
  return (dispatch) => {
    return axios
      .get(`/marketer/onboarding_status`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeOnboardingStatus(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const fetchLoansFinancingStages = (personId) => {
  return (dispatch) => {
    axios
      .get(`/api/v4/contact_people/${personId}/loans/form_options`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeFetchedLoansFinancingStages(formattedStages(res.data?.financing_stages)));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const fetchAvailableTransactions = (uuid) => {
  return (dispatch) => {
    axios
      .get(`/api/v4/people/${uuid}/agreements/available_transactions`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeFetchedAvailableTransactions(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const fetchAvailableTransactionsForLoan = (uuid) => {
  return (dispatch) => {
    axios
      .get(`/api/v4/people/${uuid}/agreements/available_transactions`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeFetchedAvailableTransactionsForLoan(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const handleAgreementCreate = (uuid, agreementData) => {
  return (dispatch) => {
    axios
      .post(`/api/v4/people/${uuid}/agreements`, {
        agreement: agreementData,
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        dispatch(agreementUploaded(createAgreements([res.data])));
        dispatch(uploadingDocument(false));
        if (res.data?.document?.id) {
          dispatch(documentUploaded(res.data?.document));
        }
        const toastData = {
          show: true,
          msg: `${agreementData.agreement_type} Agreement added`,
          ...(agreementData.isCreateTransaction &&
            agreementData.listing_id && { transactionMsg: "Transaction created" }),
        };
        dispatch(toggleSuccessToastMsg(toastData));

        setTimeout(() => {
          dispatch(toggleSuccessToastMsg({ show: false, msg: "", transactionMsg: "" }));
        }, 7000);
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const handleAgreementUpdate = (person, agreementData, agreement_id) => {
  return async (dispatch) => {
    try {
      const res = await axios.put(`/api/v4/people/${person.uuid}/agreements/${agreement_id}`, {
        agreement: agreementData,
        authenticity_token: ReactOnRails.authenticityToken(),
      });

      dispatch(agreementUpdated(createAgreements([res.data]), agreement_id));
      return res.data;
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      return { errors: err?.response?.data?.errors };
    }
  };
};

export const handleLoanUpdate = (personId, loanData, loan_id) => {
  return async (dispatch) => {
    try {
      const res = await axios.put(`/api/v4/contact_people/${personId}/loans/${loan_id}`, {
        ...loanData,
        authenticity_token: ReactOnRails.authenticityToken(),
      });

      dispatch(loanUpdated([res.data], loan_id));
      return res.data;
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      return { errors: err?.response?.data?.errors };
    }
  };
};

export const handleDocumentUpload = (fileObj, person, isAgreement, payloadData, isCreatingAgreementDoc) => {
  return async (dispatch) => {
    try {
      dispatch(uploadingDocument(true));
      const fileUploaded = fileObj;
      const url = "/people/" + person.slug + "/documents";
      let form_data = new FormData();
      form_data.append("authenticity_token", ReactOnRails.authenticityToken());
      form_data.append("document[object]", fileUploaded);
      form_data.append("person_details_page", "true");
      if (isCreatingAgreementDoc) {
        form_data.append("document[document_type]", "agreement");
        form_data.append("document[document_sub_type]", payloadData.agreement_type);
      }
      const res = await axios.post(url, form_data);

      if (isAgreement) {
        let listingId = payloadData.listing_id;
        if (!listingId) {
          listingId = payloadData.isCreateTransaction
            ? await dispatch(createTransactions(payloadData.transactionData))
            : payloadData.associatedTransaction?.value || null;
        }
        const agreementData = {
          ...payloadData,
          agreement_type: payloadData.agreement_type,
          document_id: res.data.id,
          listing_id: listingId,
          isCreateTransaction: payloadData.isCreateTransaction,
        };
        dispatch(handleAgreementCreate(person.uuid, agreementData));
      } else {
        dispatch(documentUploaded(res.data));
        dispatch(uploadingDocument(false));
      }

      return { CreatedDocID: res.data.id };
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      dispatch(uploadingDocument(false));
      return { errors: err?.response?.data?.errors };
    }
  };
};

export const handleAddendumUpload = (fileObj, person, agreement_id) => {
  return async (dispatch) => {
    try {
      dispatch(uploadingDocument(true));
      const fileUploaded = fileObj;
      const url = `/api/v4/people/${person.uuid}/agreements/${agreement_id}/documents`;
      let form_data = new FormData();
      form_data.append("authenticity_token", ReactOnRails.authenticityToken());
      form_data.append("document[object]", fileUploaded);
      form_data.append("person_details_page", "true");
      form_data.append("document[document_type]", "addendum");
      const res = await axios.post(url, form_data);
      dispatch(documentUploaded(res.data));
      dispatch(uploadingDocument(false));

      dispatch(toggleSuccessToastMsg({ show: true, msg: "Addendum uploaded" }));

      setTimeout(() => {
        dispatch(toggleSuccessToastMsg({ show: false, msg: "" }));
      }, 7000);
      return res.data;
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      dispatch(uploadingDocument(false));
      return { errors: err?.response?.data?.errors };
    }
  };
};

export const handleDocumentRename = (person, document_id, newName) => {
  return async (dispatch) => {
    try {
      const res = await axios.patch(`/people/${person.id}/documents/${document_id}/rename`, {
        file_name: newName,
        person_details_page: "true",
        authenticity_token: ReactOnRails.authenticityToken(),
      });

      dispatch(renameDocument(document_id, newName));
      return res.data;
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      return { errors: err?.response?.data?.errors };
    }
  };
};
export const handleAddendumRename = (person, agreement_id, document_id, newName) => {
  return async (dispatch) => {
    try {
      const res = await axios.patch(
        `/api/v4/people/${person.uuid}/agreements/${agreement_id}/documents/${document_id}/rename`,
        {
          file_name: newName,
          authenticity_token: ReactOnRails.authenticityToken(),
        },
      );

      dispatch(renameDocument(document_id, newName));
      return res.data;
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      return { errors: err?.response?.data?.errors };
    }
  };
};

export const handleDocumentDelete = (person, document_id) => {
  return async (dispatch) => {
    try {
      const path = `/people/${person.id}/documents/${document_id}`;
      const res = await axios.delete(path, {
        params: { authenticity_token: ReactOnRails.authenticityToken() },
      });

      dispatch(removeDeletedDocument(document_id));
      dispatch(removeDocAgreement(document_id));

      return res.data;
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      return { errors: err?.response?.data?.errors };
    }
  };
};

export const handleAddendumDelete = (person, agreement_id, document_id) => {
  return async (dispatch) => {
    try {
      const path = `/api/v4/people/${person.uuid}/agreements/${agreement_id}/documents/${document_id}`;

      const res = await axios.delete(path, {
        params: { authenticity_token: ReactOnRails.authenticityToken() },
      });

      dispatch(removeDeletedDocument(document_id));
      return res.data;
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      return { errors: err?.response?.data?.errors };
    }
  };
};

export const handleAgreementDelete = (person, agreement_id) => {
  return (dispatch) => {
    const path = `/api/v4/people/${person.data.attributes.uuid}/agreements/${agreement_id}`;
    axios
      .delete(path, { params: { authenticity_token: ReactOnRails.authenticityToken() } })
      .then((res) => {
        dispatch(removeDeletedAgreement(agreement_id));

        dispatch(toggleSuccessToastMsg({ show: true, msg: "Agreement deleted" }));

        setTimeout(() => {
          dispatch(toggleSuccessToastMsg({ show: false, msg: "" }));
        }, 7000);
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

const storeFetchedRelationships = (data) => {
  return {
    type: "FETCH_RELATIONSHIPS",
    data: data,
  };
};

export const fetchRelationships = (person) => {
  return (dispatch) => {
    axios
      .get(`/people/${person.id}/relationships?view_type=new`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeFetchedRelationships(res.data));
      });
  };
};

const relationshipAdded = (data) => {
  return {
    type: "RELATIONSHIP_ADDED",
    data: data,
  };
};

const relationshipEdited = (data) => {
  return {
    type: "RELATIONSHIP_EDITED",
    data: data,
  };
};

export const addRelationship = (relationship) => {
  return (dispatch) => {
    dispatch(relationshipAdded(relationship));
  };
};

export const saveRelationship = (person, relationshipId, primaryPersonLabel, relatedPersonLabel) => {
  return (dispatch) => {
    let editRelationshipUrl = `/people/${person.slug}/relationships/${relationshipId}.json`;
    let form_data = new FormData();
    form_data.append("authenticity_token", ReactOnRails.authenticityToken());
    form_data.append("_method", "patch");
    if (primaryPersonLabel) {
      form_data.append("relationship[primary_person_label]", primaryPersonLabel);
    }
    if (relatedPersonLabel) {
      form_data.append("relationship[related_person_label]", relatedPersonLabel);
    }
    form_data.append("person_details_page", "true");
    return axios
      .post(editRelationshipUrl, form_data)
      .then((res) => {
        dispatch(relationshipEdited(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

const storeFetchedTransactions = (desc) => {
  return {
    type: "STORE_FETCHED_TRANSACTIONS",
    data: desc,
  };
};

export const getPerson = (slug) => (dispatch) => {
  dispatch(togglePersonError());
  axios
    .get(`/people/${slug}/details.json`)
    .then(({ data }) => dispatch(updatePersonSuccess(data)))
    .catch((err) => dispatch(togglePersonError(err?.response?.data || true)));
};

export const getPersonDetails = (slug) => (dispatch) => {
  dispatch(togglePersonError());
  axios
    .get(`/api/v4/person/${slug}/details`)
    .then(({ data }) => dispatch(updatePersonSuccess(data)))
    .catch((err) => dispatch(togglePersonError(err?.response?.data || true)));
};

export const getTagsList = () => (dispatch) => {
  dispatch(toggleTagsError());
  axios
    .get("/tags/list")
    .then(({ data }) => dispatch(updateTagsSuccess(data)))
    .catch((err) => dispatch(toggleTagsError(err?.response?.data || true)));
};

export const getNavLinks = (slug) => (dispatch) =>
  caseTransformingAxios
    .get(`/api/v4/person/${slug}/nav_links`)
    .then(({ data }) => dispatch({ type: "GET_NAV_LINKS", data }))
    .catch(console.log);

export const fetchTransactions = (person_id) => {
  return (dispatch) => {
    axios
      .get(`/people/${person_id}/transactions/quick_view`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(storeFetchedTransactions(res.data));
      });
  };
};

export const openTaskModal = () => ({ type: "OPEN_TASK_MODAL" });

export const closeTaskModal = () => ({ type: "CLOSE_TASK_MODAL" });

export const openAddListingAlertModal = () => ({ type: "OPEN_ADD_LISTING_ALERT_MODAL" });

export const closeAddListingAlertModal = () => ({ type: "CLOSE_ADD_LISTING_ALERT_MODAL" });

export const deleteRelationship = (person, relationshipId) => {
  return (dispatch) => {
    const path = `/people/${person.slug}/relationships/${relationshipId}?view_type=new`;
    axios
      .delete(path, {
        params: { person_details_page: true, authenticity_token: ReactOnRails.authenticityToken() },
      })
      .then((res) => {
        dispatch(relationShipDestroyed(relationshipId));
      })
      .catch((error) => {
        console.log(error);
      });
  };
};

const relationShipDestroyed = (relationshipId) => {
  return {
    type: "RELATIONSHIP_DELETED",
    data: relationshipId,
  };
};

export const openEditSocialMediaModal = () => {
  return {
    type: "OPEN_EDIT_SOCIAL_MEDIA_MODAL",
  };
};

export const closeEditSocialMediaModal = () => {
  return {
    type: "CLOSE_EDIT_SOCIAL_MEDIA_MODAL",
  };
};

export const openWebActivityModal = () => ({ type: "OPEN_WEB_ACTIVITY_MODAL" });
export const closeWebActivityModal = () => ({ type: "CLOSE_WEB_ACTIVITY_MODAL" });

export const openAddAutoPlanModal = () => ({ type: "OPEN_ADD_AUTO_PLAN_MODAL" });

export const closeAddAutoPlanModal = () => ({ type: "CLOSE_ADD_AUTO_PLAN_MODAL" });

export const closeDeleteAssignedPlanModal = () => ({ type: "CLOSE_DELETE_ASSIGNED_PLAN_MODAL" });

const fetchAssignedPlansSuccess = (assignedPlans) => {
  return {
    type: "FETCH_ASSIGNED_PLANS",
    data: assignedPlans,
  };
};

const assignedPlanSuccess = (assignedPlan) => {
  return {
    type: "CREATE_ASSIGNED_PLAN",
    data: assignedPlan,
  };
};

const updateAssignedPlanSuccess = (assignedPlan) => {
  return {
    type: "UPDATE_ASSIGNED_PLAN",
    data: assignedPlan,
  };
};

export const getAssignedPlans = (assignable_id) => {
  return (dispatch) => {
    // dispatch(fetchAssignedPlansPending());

    axios
      .get("/assigned_plans", {
        responseType: "json",
        contentType: "application/json",
        params: { assignable_type: "Person", assignable_id },
      })
      .then((results) => {
        dispatch(fetchAssignedPlansSuccess(results.data));
      })
      .catch((err) => {
        console.log("error", err);
        // dispatch(fetchAssignedPlansFailure());
      });
  };
};

export const fetchAutoPlans = (autoPlansPath, type, page = null) => {
  const path = `${autoPlansPath}.json?plan_type=${type}&page=${page}`;
  return (dispatch) => {
    // dispatch(fetchAutoPlans())
    axios
      .get(path)
      .then((results) => {
        dispatch(autoPlansSuccess(results.data));
      })
      .catch((err) => {
        dispatch({ type: "AUTO_PLANS_FAILURE", isFetchingAutoPlans: false });
      });
  };
};

export const submitApplyPlan = (plan, assignableId) => {
  const form = { ...plan, assignable_id: assignableId, assignable_type: "Person", auto_plan_id: plan.id };

  return (dispatch) => {
    axios
      .post("/assigned_plans", {
        assigned_plan: form,
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((results) => {
        dispatch(assignedPlanSuccess(results.data));
      });
  };
};

export const updateAssignedPlan = (assignedPlanPath, form) => {
  const formWithToken = { ...form, authenticity_token: ReactOnRails.authenticityToken() };

  return (dispatch) => {
    axios
      .patch(assignedPlanPath, formWithToken)
      .then((results) => {
        dispatch(updateAssignedPlanSuccess(results.data.assigned_plan));
      })
      .catch((err) => console.log("ERROR UPDATING PLAN", err));
  };
};

export const openDeleteAssignedPlanModal = (planToDelete) => {
  return (dispatch) => {
    dispatch({
      type: "OPEN_DELETE_ASSIGNED_PLAN_MODAL",
      planToDelete,
    });
  };
};

export const openEditAppointmentModal = () => ({ type: "OPEN_EDIT_APPOINTMENT_MODAL" });
export const closeEditAppointmentModal = () => ({ type: "CLOSE_EDIT_APPOINTMENT_MODAL" });
export const updateAppointment = (data) => {
  return (dispatch) => {
    dispatch(updateAppointmentState(data));
  };
};

const updateAppointmentState = (data) => {
  return {
    type: "UPDATE_APPOINTMENT",
    data: data,
  };
};

export const deleteAppointment = ({ id, person_id }) => {
  return (dispatch) => {
    axios
      .delete(`/people/${person_id}/interactions/${id}`, {
        params: {
          person_details_page: "true",
          authenticity_token: ReactOnRails.authenticityToken(),
        },
      })
      .then((res) => {
        dispatch(removeAppointment(id));
      })
      .catch((err) => {
        console.log(err);
      });
  };
};

const removeAppointment = (id) => {
  return {
    type: "REMOVE_APPOINTMENT",
    id,
  };
};

export const getTasks = (person_id) => {
  return (dispatch) => {
    axios
      .get(`/tasks/quick_view?id=${person_id}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        dispatch(getTasksSuccess(res.data));
      })
      .catch((err) => {
        console.log(err);
      });
  };
};

export const getAppointments = (person_id, limit = 3) => {
  return (dispatch) => {
    axios
      .get(`/tasks/appointments?id=${person_id}&limit=${limit}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) => {
        if (limit === 100) {
          dispatch(getAllAppointmentsSuccess(res.data));
        } else {
          dispatch(getAppointmentsSuccess(res.data));
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };
};

export const deleteTask = (taskId, personId, limit) => {
  return (dispatch) => {
    axios
      .delete(`/tasks/${taskId}`, { params: { authenticity_token: ReactOnRails.authenticityToken() } })
      .then((res) => {
        dispatch(getAppointments(personId, limit));
      })
      .catch((err) => {
        console.log(err);
      });
  };
};

const getTasksSuccess = (data) => {
  return {
    type: "GET_TASKS",
    data,
  };
};

const getAppointmentsSuccess = (data) => {
  return {
    type: "GET_APPOINTMENTS",
    data,
  };
};

const getAllAppointmentsSuccess = (data) => {
  return {
    type: "GET_ALL_APPOINTMENTS",
    data,
  };
};

export const updateTasks = () => {
  return {
    type: "UPDATE_TASKS",
  };
};

export const updateCardTasks = (task) => {
  return {
    type: "UPDATE_CARD_TASKS",
    task,
  };
};

const getNotesSuccess = (data) => {
  return {
    type: "GET_NOTES",
    data,
  };
};

export const showAutoPlanPreview = (auto_plan_id) => {
  return (dispatch) => {
    axios
      .get(`/auto_plans/${auto_plan_id}/preview`)
      .then((res) => dispatch({ type: "SHOW_AUTO_PLAN_PREVIEW", autoPlan: res.data.auto_plan }))
      .catch((err) => {
        console.log("Failed to preview auto plan");
      });
  };
};

export const previewAction = (actionId) => {
  return (dispatch) => {
    dispatch({
      type: "PREVIEW_ACTION",
      actionId,
    });
  };
};

export const closeAutoPlanPreview = () => {
  return {
    type: "CLOSE_AUTO_PLAN_PREVIEW",
  };
};

export const showPlanDetails = (planId) => {
  return (dispatch) => {
    axios
      .get(`/assigned_plans/${planId}`, {
        responseType: "json",
        contentType: "application/json",
      })
      .then((results) => {
        dispatch({ type: "SHOW_PLAN_DETAILS", assignedPlan: results.data.assigned_plan });
      })
      .catch((err) => {
        console.log("Can't show assigned plan modal");
      });
  };
};

export const closePlanDetails = () => {
  return {
    type: "CLOSE_PLAN_DETAIL",
  };
};

export const assignedActionPreview = (assignedAction) => {
  return (dispatch) => {
    dispatch({
      type: "ASSIGNED_ACTION_PREVIEW",
      assignedAction,
    });
  };
};

export const showAssignedActionDeleteModal = (actionToDelete) => {
  return (dispatch) => {
    dispatch({
      type: "SHOW_ASSIGNED_ACTION_DELETE_MODAL",
      actionToDelete,
    });
  };
};

export const closeAssignedActionDeleteModal = () => {
  return {
    type: "CLOSE_ASSIGNED_ACTION_DELETE_MODAL",
  };
};

export const deleteAction = (id, assignedActionPath, callback) => {
  return (dispatch) => {
    axios
      .delete(assignedActionPath, { params: { authenticity_token: ReactOnRails.authenticityToken() } })
      .then((results) => {
        dispatch({
          type: "DELETE_ACTION_SUCCESS",
          assignedActionId: id,
        });
        // TODO: is this used?
        if (callback) {
          dispatch(callback);
        }
      })
      .catch((err) => {
        dispatch({
          type: "DELETE_ACTION_FAILURE",
          error: err.response.data.message,
        });
      });
  };
};

export const updateAction = (assignedActionPath, form = {}) => {
  const formWithToken = { ...form, authenticity_token: ReactOnRails.authenticityToken() };

  return (dispatch) => {
    dispatch(updateActionPending());

    axios
      .patch(assignedActionPath, formWithToken)
      .then((results) => {
        dispatch(updateActionSuccess(results.data));
      })
      .catch((err) => {
        dispatch(updateActionFailure());
      });
  };
};

const updateActionPending = () => {
  return {
    type: "UPDATE_ACTION_PENDING",
  };
};

const updateActionSuccess = (assignedAction) => {
  return {
    type: "UPDATE_ACTION_SUCCESS",
    assignedAction,
  };
};

const updateActionFailure = (assignedAction) => {
  return {
    type: "UPDATE_ACTION_FAILURE",
  };
};

export const openDeleteCloseActionsModal = (planToDelete) => {
  return (dispatch) => {
    dispatch({
      type: "OPEN_DELETE_CLOSE_ACTIONS_MODAL",
      planToDelete,
    });
  };
};

export const setInteractionTab = (tabIndex) => {
  return (dispatch) => {
    dispatch({
      type: "SET_INTERACTION_TAB",
      tabIndex,
    });
  };
};

export const setMessageUUID = (messageUUID) => {
  return (dispatch) => {
    dispatch({
      type: "SET_MESSAGE_UUID",
      messageUUID,
    });
  };
};

export const setGmailReplyThread = (
  gmailReplyThreadId,
  gmailReplyThreadSubject,
  additionalRecipientsVal,
  CCval,
  BCCval,
) => {
  return (dispatch) => {
    dispatch({
      type: "SET_GMAIL_REPLY_THREAD",
      gmailReplyThreadId,
      gmailReplyThreadSubject,
      additionalRecipientsVal,
      CCval,
      BCCval,
    });
  };
};

export const setInteractionTabWithHomeAppContent = (defaultHomeAppTabContent, tabIndex) => {
  return {
    type: "SET_INTERACTION_TAB_WITH_CONTENT",
    defaultHomeAppTabContent,
    tabIndex,
  };
};

export const clearInteractionTabContent = () => {
  return {
    type: "CLEAR_INTERACTION_TAB_CONTENT",
  };
};

export const touchHomeAppInviteSent = (personId) => {
  return (dispatch) => {
    axios
      .patch(`/api/v4/person_detail/${personId}/touch_home_app_invite_sent`, {
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        dispatch(updateHomeAppInviteSent(res.data));
      });
  };
};

const updateHomeAppInviteSent = (personData) => {
  return {
    type: "UPDATE_HOME_APP_INVITE_SENT",
    id: personData.data.attributes.id,
    inviteSent: personData.data.attributes.home_app_invite_sent,
  };
};

export const toggleTransactionModal = (value) => {
  return {
    type: "TOGGLE_TRANSACTION_MODAL",
    value,
  };
};

export const toggleLenderModal = () =>
  wrap({
    type: "TOGGLE_LENDER_MODAL",
  });

export const toggleSuccessReferLoanToast = () =>
  wrap({
    type: "TOGGLE_SUCCESS_REFER_LOAN_TOAST",
  });

export const setLastRefferedDateTime = (dateTime) =>
  wrap({
    type: "SET_LAST_REFERRED_DATE_TIME",
    data: dateTime,
  });

export const toggleStarred = (note) => {
  return async (dispatch) => {
    axios
      .patch(`/people/${note.notable_id}/notes/${note.id}`, {
        note: { ...note, starred: !note.starred },
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        dispatch(updateNoteSuccess(res.data));
        dispatch(updateTimelineEvent(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
      });
  };
};

export const toggleExternallyVisible = (note) => {
  return async (dispatch) => {
    axios
      .patch(`/people/${note.notable_id}/notes/${note.id}`, {
        note: { ...note, externallyVisible: !note.externallyVisible },
        authenticity_token: ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        dispatch(updateNoteSuccess(res.data));
        dispatch(updateTimelineEvent(res.data));
      })
      .catch((err) => {
        // TODO: Handle error
        conssole.log(err);
      });
  };
};

export const fetchProjects = (slug) => (dispatch) =>
  axios
    .get(`/api/v4/person/${slug}/projects`)
    .then(({ data }) => dispatch({ type: "FETCH_PROJECTS", data }))
    .catch(console.log);
